<template>
  <div class="running-detail-page">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading workflow details...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <div class="error-icon">⚠️</div>
      <h2>Failed to Load Workflow</h2>
      <p>{{ error }}</p>
      <div class="error-actions">
        <button @click="loadWorkflowDetails" class="btn-primary">Try Again</button>
        <button @click="$router.push('/admin/scout/workflow/running')" class="btn-secondary">
          Back to Running Workflows
        </button>
      </div>
    </div>

    <div v-else-if="workflow" class="workflow-content">
      <!-- Header -->
      <div class="workflow-header">
        <button @click="$router.push('/admin/scout/workflow/running')" class="btn-back">
          ← Back to Running Workflows
        </button>
        
        <!-- Workflow control buttons -->
        <div class="workflow-controls">
          <button 
            v-if="workflow.status === 'running'"
            @click="confirmPauseWorkflow"
            class="btn-control pause"
            title="Pause Workflow"
          >
            <i class="bi bi-pause-circle"></i> Pause
          </button>
          <button 
            v-if="workflow.status === 'paused'"
            @click="resumeWorkflow"
            class="btn-control resume"
            title="Resume Workflow"
          >
            <i class="bi bi-play-circle"></i> Resume
          </button>
          <button 
            @click="confirmKillWorkflow"
            class="btn-control kill"
            title="Kill Workflow"
          >
            <i class="bi bi-x-circle"></i> Kill
          </button>
          <button 
            @click="toggleAutoRefresh"
            class="btn-control"
            :class="{ 'active': autoRefreshEnabled }"
            :title="autoRefreshEnabled ? 'Disable Auto-Refresh' : 'Enable Auto-Refresh'"
          >
            <i class="bi bi-arrow-clockwise"></i> Auto-Refresh {{ autoRefreshEnabled ? 'ON' : 'OFF' }}
          </button>
        </div>
      </div>

      <!-- Main Info Card -->
      <div class="info-card">
        <div class="info-card-header">
          <div>
            <h1>{{ workflow.workflow_name }}</h1>
            <div class="workflow-meta">
              <span class="workflow-uuid">
                <span class="uuid-label">Execution UUID:</span>
                {{ workflow.execution_uuid }}
                <CopyButton :text="workflow.execution_uuid" />
              </span>
              <span v-if="workflow.context_uuid" class="context-uuid">
                <span class="uuid-label">Context UUID:</span>
                {{ workflow.context_uuid }}
                <CopyButton :text="workflow.context_uuid" />
              </span>
            </div>
          </div>
          <StatusBadge :status="workflow.status" size="large" />
        </div>

        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">
              <i class="bi bi-clock-history"></i>
              Running for
            </span>
            <span class="info-value duration">{{ formatDuration(calculateRunningDuration()) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">
              <i class="bi bi-hdd-stack"></i>
              Environment
            </span>
            <span class="info-value">
              <span :class="`env-badge env-${workflow.env}`">
                {{ workflow.env.toUpperCase() }}
              </span>
            </span>
          </div>
          <div class="info-item">
            <span class="info-label">
              <i class="bi bi-play-circle"></i>
              Started
            </span>
            <span class="info-value">{{ formatTimestamp(workflow.start_time) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">
              <i class="bi bi-tag"></i>
              Type
            </span>
            <span class="info-value">{{ workflow.type }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">
              <i class="bi bi-fingerprint"></i>
              Ref
            </span>
            <span class="info-value uuid-text">
              {{ truncateUuid(workflow.ref, 16) }}
              <CopyButton :text="workflow.ref" />
            </span>
          </div>
          <div v-if="workflow.current_node_id" class="info-item">
            <span class="info-label">
              <i class="bi bi-cpu"></i>
              Current Node
            </span>
            <span class="info-value">{{ getCurrentNodeName() }}</span>
          </div>
        </div>

        <!-- Progress indicator -->
        <div class="progress-section">
          <div class="progress-label">
            <span>Progress</span>
            <span>{{ getCompletedNodesCount() }} / {{ (workflow.execution_nodes || []).length }} nodes completed</span>
          </div>
          <div class="progress-bar">
            <div 
              class="progress-fill"
              :style="{ width: getProgressPercentage() + '%' }"
            ></div>
          </div>
        </div>
      </div>

      <!-- Input Data -->
      <div class="section-card">
        <div class="section-header" @click="toggleSection('input')">
          <h3>
            <i class="bi bi-chevron-right" :class="{ 'rotated': sections.input }"></i>
            <i class="bi bi-braces"></i>
            Input Data
          </h3>
        </div>
        <div v-if="sections.input" class="section-content json-viewer-wrapper">
          <JsonViewer :value="workflow.input_data" :expand-depth="1" copyable sort></JsonViewer>
        </div>
      </div>

      <!-- Node Execution Timeline -->
      <!-- Node Timeline -->
      <div class="section-card">
        <div class="section-header">
          <h3>
            <i class="bi bi-diagram-3"></i>
            Node Execution Timeline
          </h3>
          <span class="node-count">
            <i class="bi bi-boxes"></i>
            {{ visibleNodes.length }} / {{ sortedNodes.length }} nodes
          </span>
        </div>
        <div class="section-content">
          <div class="nodes-container">
            <NodeCard 
              v-for="nodeItem in visibleNodes" 
              :key="nodeItem.uniqueKey"
              :node="nodeItem.node"
              :node-unique-key="nodeItem.uniqueKey"
              :depth="nodeItem.depth"
              :has-children="nodeItem.children.length > 0"
              :children-count="nodeItem.childrenCount"
              :show-controls="true"
              :workflow-status="workflow.status"
              @pause-node="confirmPauseNode"
              @resume-node="resumeNode"
              @kill-node="confirmKillNode"
              @toggle-children="toggleNodeChildren"
            />
          </div>
        </div>
      </div>      <!-- Execution Logs -->
      <div class="section-card">
        <div class="section-header">
          <h3>
            <i class="bi bi-file-text"></i>
            Execution Logs
          </h3>
          <span class="log-count">
            <i class="bi bi-list-ul"></i>
            {{ (workflow.logs || []).length }} entries
          </span>
        </div>
        <div class="section-content">
          <LogViewer :logs="workflow.logs" />
        </div>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <ConfirmModal
      :show="confirmModal.show"
      :title="confirmModal.title"
      :message="confirmModal.message"
      :confirm-text="confirmModal.confirmText"
      :danger-button="confirmModal.danger"
      :icon="confirmModal.icon"
      @confirm="handleConfirmAction"
      @cancel="closeConfirmModal"
    />
  </div>
</template>

<script>
import WorkflowHistory from '@/lib/WorkflowHistory';
import StatusBadge from '@/components/workflow/StatusBadge.vue';
import CopyButton from '@/components/workflow/CopyButton.vue';
import NodeCard from '@/components/workflow/NodeCard.vue';
import LogViewer from '@/components/workflow/LogViewer.vue';
import ConfirmModal from '@/components/workflow/ConfirmModal.vue';
import JsonViewer from 'vue-json-viewer';
import { 
  formatDuration, 
  formatTimestamp, 
  truncateUuid 
} from '@/shared/workflowHistoryUtils';

export default {
  name: 'RunningWorkflowDetail',
  components: {
    StatusBadge,
    CopyButton,
    NodeCard,
    LogViewer,
    ConfirmModal,
    JsonViewer
  },
  data() {
    return {
      workflow: null,
      loading: false,
      error: null,
      workflowHistoryService: null,
      collapsedNodes: new Set(),
      sections: {
        input: false
      },
      autoRefreshEnabled: true,
      refreshInterval: 5000, // 5 seconds
      refreshTimer: null,
      confirmModal: {
        show: false,
        title: '',
        message: '',
        confirmText: 'Confirm',
        danger: false,
        icon: 'warning',
        action: null,
        params: null
      }
    };
  },
  computed: {
    sortedNodes() {
      if (!this.workflow || !this.workflow.execution_nodes) return [];
      
      // Build a hierarchical tree structure
      const nodesMap = new Map();
      const rootNodes = [];
      
      // First pass: create all nodes with depth and children array
      // Use index as unique key since nodes can have same id but different parent_node_id
      this.workflow.execution_nodes.forEach((nodeItem, index) => {
        const uniqueKey = `${nodeItem.node.id}-${nodeItem.parent_node_id || 'root'}-${index}`;
        nodesMap.set(uniqueKey, {
          ...nodeItem,
          uniqueKey,
          children: [],
          depth: 0
        });
      });
      
      // Create a map to find children by parent_node_id
      const nodesByParentId = new Map();
      this.workflow.execution_nodes.forEach((nodeItem, index) => {
        const uniqueKey = `${nodeItem.node.id}-${nodeItem.parent_node_id || 'root'}-${index}`;
        const parentKey = nodeItem.parent_node_id;
        if (!nodesByParentId.has(parentKey)) {
          nodesByParentId.set(parentKey, []);
        }
        nodesByParentId.get(parentKey).push({ nodeItem, uniqueKey });
      });
      
      // Second pass: build parent-child relationships and calculate depth
      this.workflow.execution_nodes.forEach((nodeItem, index) => {
        const uniqueKey = `${nodeItem.node.id}-${nodeItem.parent_node_id || 'root'}-${index}`;
        const node = nodesMap.get(uniqueKey);
        
        if (nodeItem.parent_node_id) {
          // Find parent node by matching node.id === parent_node_id
          let parentNode = null;
          for (const value of nodesMap.values()) {
            if (value.node.id === nodeItem.parent_node_id) {
              parentNode = value;
              break;
            }
          }
          
          if (parentNode) {
            parentNode.children.push(node);
            node.depth = parentNode.depth + 1;
          } else {
            rootNodes.push(node);
          }
        } else {
          rootNodes.push(node);
        }
      });
      
      // Count total descendants for each node
      const countDescendants = (node) => {
        let count = node.children.length;
        node.children.forEach(child => {
          count += countDescendants(child);
        });
        node.childrenCount = count;
        return count;
      };
      
      nodesMap.forEach(node => countDescendants(node));
      
      // Flatten tree in hierarchical order (depth-first traversal)
      const flattenTree = (node) => {
        const result = [node];
        // Sort children by start time before flattening
        const sortedChildren = [...node.children].sort((a, b) => {
          const aTime = (a.node.state && a.node.state.starttime) ? new Date(a.node.state.starttime) : new Date(0);
          const bTime = (b.node.state && b.node.state.starttime) ? new Date(b.node.state.starttime) : new Date(0);
          return aTime - bTime;
        });
        sortedChildren.forEach(child => {
          result.push(...flattenTree(child));
        });
        return result;
      };
      
      // Sort root nodes by start time
      const sortedRoots = rootNodes.sort((a, b) => {
        const aTime = (a.node.state && a.node.state.starttime) ? new Date(a.node.state.starttime) : new Date(0);
        const bTime = (b.node.state && b.node.state.starttime) ? new Date(b.node.state.starttime) : new Date(0);
        return aTime - bTime;
      });
      
      // Flatten all trees
      return sortedRoots.flatMap(flattenTree);
    },
    visibleNodes() {
      // Filter nodes based on collapsed state
      return this.sortedNodes.filter(nodeItem => {
        // Always show root nodes
        if (nodeItem.depth === 0) return true;
        
        // Check if any ancestor is collapsed using uniqueKey
        let parentId = nodeItem.parent_node_id;
        
        while (parentId) {
          // Find the parent node's uniqueKey
          const parentNode = this.sortedNodes.find(n => n.node.id === parentId);
          if (parentNode && this.collapsedNodes.has(parentNode.uniqueKey)) {
            return false;
          }
          // Move to next parent
          parentId = parentNode ? parentNode.parent_node_id : null;
        }
        
        return true;
      });
    }
  },
  created() {
    this.workflowHistoryService = new WorkflowHistory(this.$workflowApi);
    this.loadWorkflowDetails();
  },
  mounted() {
    if (this.autoRefreshEnabled) {
      this.startAutoRefresh();
    }
  },
  beforeDestroy() {
    this.stopAutoRefresh();
  },
  methods: {
    formatDuration,
    formatTimestamp,
    truncateUuid,
    
    toggleNodeChildren(uniqueKey, isCollapsed) {
      if (isCollapsed) {
        this.collapsedNodes.add(uniqueKey);
      } else {
        this.collapsedNodes.delete(uniqueKey);
      }
      // Force reactivity
      this.collapsedNodes = new Set(this.collapsedNodes);
    },
    
    async loadWorkflowDetails() {
      const executionUuid = this.$route.params.executionUuid;
      
      if (!executionUuid) {
        this.error = 'No execution UUID provided';
        return;
      }
      
      // Only show loading spinner on initial load, not on refresh
      if (!this.workflow) {
        this.loading = true;
      }
      this.error = null;
      
      try {
        const data = await this.workflowHistoryService.fetchRunningWorkflowDetails(executionUuid);
        this.workflow = data;
      } catch (err) {
        if (err.response && err.response.status === 404) {
          this.error = 'Running workflow not found. It may have completed.';
        } else {
          this.error = err.message || 'Failed to load workflow details';
        }
        console.error('Error loading running workflow details:', err);
        this.stopAutoRefresh();
      } finally {
        this.loading = false;
      }
    },
    
    toggleSection(section) {
      this.sections[section] = !this.sections[section];
    },
    
    formatJson(data) {
      return JSON.stringify(data, null, 2);
    },

    calculateRunningDuration() {
      if (!this.workflow) return 0;
      const start = new Date(this.workflow.start_time);
      const now = new Date();
      return now.getTime() - start.getTime();
    },

    getCurrentNodeName() {
      if (!this.workflow.current_node_id) return 'N/A';
      if (!this.workflow.execution_nodes) return `Node ${this.workflow.current_node_id}`;
      const nodeItem = this.workflow.execution_nodes.find(n => n.node.id === this.workflow.current_node_id);
      return nodeItem ? nodeItem.node.name : `Node ${this.workflow.current_node_id}`;
    },

    getCompletedNodesCount() {
      if (!this.workflow || !this.workflow.execution_nodes) return 0;
      return this.workflow.execution_nodes.filter(nodeItem => {
        const state = nodeItem.node.State || nodeItem.node.state;
        return state && state.status === 'completed';
      }).length;
    },

    getProgressPercentage() {
      if (!this.workflow || !this.workflow.execution_nodes || this.workflow.execution_nodes.length === 0) return 0;
      return Math.round((this.getCompletedNodesCount() / this.workflow.execution_nodes.length) * 100);
    },

    toggleAutoRefresh() {
      this.autoRefreshEnabled = !this.autoRefreshEnabled;
      if (this.autoRefreshEnabled) {
        this.startAutoRefresh();
      } else {
        this.stopAutoRefresh();
      }
    },

    startAutoRefresh() {
      this.stopAutoRefresh();
      this.refreshTimer = setInterval(() => {
        this.loadWorkflowDetails();
      }, this.refreshInterval);
    },

    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },

    confirmPauseWorkflow() {
      this.confirmModal = {
        show: true,
        title: 'Pause Workflow',
        message: `Are you sure you want to pause the workflow "${this.workflow.workflow_name}"? This will pause execution until you resume it.`,
        confirmText: 'Pause Workflow',
        danger: false,
        icon: 'warning',
        action: 'pauseWorkflow',
        params: null
      };
    },

    async pauseWorkflow() {
      try {
        await this.workflowHistoryService.pauseWorkflow(this.workflow.execution_uuid);
        // Update status immediately
        this.workflow.status = 'paused';
        this.$bvToast.toast('Workflow paused successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to pause workflow', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        console.error('Error pausing workflow:', error);
      }
    },

    async resumeWorkflow() {
      try {
        await this.workflowHistoryService.resumeWorkflow(this.workflow.execution_uuid);
        // Update status immediately
        this.workflow.status = 'running';
        this.$bvToast.toast('Workflow resumed successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to resume workflow', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        console.error('Error resuming workflow:', error);
      }
    },

    confirmKillWorkflow() {
      this.confirmModal = {
        show: true,
        title: 'Kill Workflow',
        message: `Are you sure you want to kill the workflow "${this.workflow.workflow_name}"? This action cannot be undone.`,
        confirmText: 'Kill Workflow',
        danger: true,
        icon: 'danger',
        action: 'killWorkflow',
        params: null
      };
    },

    async killWorkflow() {
      try {
        await this.workflowHistoryService.killWorkflow(this.workflow.execution_uuid);
        this.$bvToast.toast('Workflow killed successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
        this.stopAutoRefresh();
        setTimeout(() => {
          this.$router.push('/admin/scout/workflow/running');
        }, 1500);
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to kill workflow', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        console.error('Error killing workflow:', error);
      }
    },

    confirmPauseNode(nodeId) {
      const nodeItem = this.workflow.execution_nodes.find(n => n.node.id === nodeId);
      const nodeName = nodeItem ? nodeItem.node.name : `Node ${nodeId}`;
      
      this.confirmModal = {
        show: true,
        title: 'Pause Node',
        message: `Are you sure you want to pause the node "${nodeName}"? This will pause execution of this node until you resume it.`,
        confirmText: 'Pause Node',
        danger: false,
        icon: 'warning',
        action: 'pauseNode',
        params: { nodeId }
      };
    },

    async pauseNode(nodeId) {
      try {
        await this.workflowHistoryService.pauseNode(this.workflow.execution_uuid, nodeId);
        // Update node paused flag immediately
        const nodeItem = this.workflow.execution_nodes.find(n => n.node.id === nodeId);
        if (nodeItem) {
          nodeItem.node.paused = true;
        }
        this.$bvToast.toast('Node paused successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to pause node', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        console.error('Error pausing node:', error);
      }
    },

    async resumeNode(nodeId) {
      try {
        await this.workflowHistoryService.resumeNode(this.workflow.execution_uuid, nodeId);
        // Update node paused flag immediately
        const nodeItem = this.workflow.execution_nodes.find(n => n.node.id === nodeId);
        if (nodeItem) {
          nodeItem.node.paused = false;
        }
        this.$bvToast.toast('Node resumed successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to resume node', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        console.error('Error resuming node:', error);
      }
    },

    confirmKillNode(nodeId) {
      const nodeItem = this.workflow.execution_nodes.find(n => n.node.id === nodeId);
      const nodeName = nodeItem ? nodeItem.node.name : `Node ${nodeId}`;
      
      this.confirmModal = {
        show: true,
        title: 'Kill Node',
        message: `Are you sure you want to kill the node "${nodeName}"? This will also skip all dependent child nodes. This action cannot be undone.`,
        confirmText: 'Kill Node',
        danger: true,
        icon: 'danger',
        action: 'killNode',
        params: { nodeId }
      };
    },

    async killNode(nodeId) {
      try {
        await this.workflowHistoryService.killNode(this.workflow.execution_uuid, nodeId);
        this.$bvToast.toast('Node killed successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
        await this.loadWorkflowDetails();
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to kill node', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        console.error('Error killing node:', error);
      }
    },

    handleConfirmAction() {
      const { action, params } = this.confirmModal;
      if (action === 'killWorkflow') {
        this.killWorkflow();
      } else if (action === 'killNode') {
        this.killNode(params.nodeId);
      } else if (action === 'pauseWorkflow') {
        this.pauseWorkflow();
      } else if (action === 'pauseNode') {
        this.pauseNode(params.nodeId);
      }
      this.closeConfirmModal();
    },

    closeConfirmModal() {
      this.confirmModal = {
        show: false,
        title: '',
        message: '',
        confirmText: 'Confirm',
        danger: false,
        icon: 'warning',
        action: null,
        params: null
      };
    }
  }
};
</script>

<style scoped>
.running-detail-page {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--text-primary);
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid var(--border-default);
  border-top-color: var(--primary-start);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.error-state h2 {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 12px 0;
  color: var(--text-primary);
}

.error-state p {
  color: #9ca3af;
  margin: 0 0 24px 0;
}

.error-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.btn-primary,
.btn-secondary {
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
}

.btn-primary {
  background-color: var(--primary-start);
  color: var(--text-primary);
  border: 1px solid var(--primary-start);
}

.btn-primary:hover {
  background-color: hsl(243, 91%, 64%);
}

.btn-secondary {
  background-color: var(--bg-card);
  color: var(--text-primary);
  border: 1px solid var(--border-default);
}

.btn-secondary:hover {
  background-color: var(--bg-card);
}

.workflow-header {
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.workflow-controls {
  display: flex;
  gap: 8px;
}

.btn-back {
  padding: 8px 16px;
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--text-primary);
}

.btn-back:hover {
  background-color: var(--bg-card);
}

.btn-control {
  background: transparent;
  border: 1px solid #6b7280;
  color: #9ca3af;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-control:hover {
  background-color: rgba(107, 114, 128, 0.1);
  border-color: #9ca3af;
  color: var(--text-primary);
}

.btn-control.active {
  border-color: var(--primary-start);
  color: var(--primary-start);
  background-color: rgba(108, 99, 255, 0.1);
}

.btn-control.pause:hover {
  border-color: #f59e0b;
  color: #f59e0b;
  background-color: rgba(245, 158, 11, 0.1);
}

.btn-control.resume:hover {
  border-color: #10b981;
  color: #10b981;
  background-color: rgba(16, 185, 129, 0.1);
}

.btn-control.kill:hover {
  border-color: #ef4444;
  color: #ef4444;
  background-color: rgba(239, 68, 68, 0.1);
}

.info-card,
.section-card {
  background: var(--bg-card);
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  border: 1px solid var(--border-default);
}

.info-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
}

.info-card-header h1 {
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
}

.workflow-meta {
  display: flex;
  align-items: center;
  gap: 12px;
}

.workflow-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.workflow-uuid,
.context-uuid {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 13px;
  color: #9ca3af;
}

.uuid-label {
  font-weight: 600;
  color: var(--primary-start);
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.5px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-label {
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.info-label i {
  color: var(--primary-start);
  font-size: 14px;
}

.info-value {
  font-size: 16px;
  color: var(--text-primary);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-value.duration {
  color: var(--primary-start);
}

.uuid-text {
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 14px;
}

.env-badge {
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  display: inline-block;
}

.env-dev {
  background-color: var(--border-default);
  color: #fbbf24;
  border: 1px solid #fbbf24;
}

.env-stage {
  background-color: var(--border-default);
  color: #fb923c;
  border: 1px solid #fb923c;
}

.env-live {
  background-color: var(--border-default);
  color: #10b981;
  border: 1px solid #10b981;
}

.progress-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--border-default);
}

.progress-label {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 13px;
  color: #9ca3af;
  font-weight: 600;
}

.progress-bar {
  height: 8px;
  background-color: var(--form-bg);
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--primary-start), #8b5cf6);
  transition: width 0.3s ease;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
  margin-bottom: 16px;
}

.section-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-header h3 i {
  color: var(--primary-start);
}

.section-header h3 i.bi-chevron-right {
  font-size: 12px;
  transition: transform 0.2s ease;
  color: #9ca3af;
}

.section-header h3 i.bi-chevron-right.rotated {
  transform: rotate(90deg);
}

.node-count,
.log-count {
  font-size: 14px;
  color: #9ca3af;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}

.node-count i,
.log-count i {
  color: var(--primary-start);
}

.section-content {
  margin-top: 16px;
}

.section-content pre {
  background-color: var(--form-bg);
  border: 1px solid var(--border-default);
  color: var(--text-primary);
  padding: 16px;
  border-radius: 6px;
  overflow-x: auto;
  font-size: 13px;
  margin: 0;
}

.json-viewer-wrapper {
  background-color: var(--form-bg);
  border: 1px solid var(--border-default);
  padding: 16px;
  border-radius: 6px;
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  font-size: 13px;
}

/* Vue JSON Viewer Dark Theme Overrides */
.json-viewer-wrapper >>> .jv-container {
  background-color: var(--form-bg) !important;
  color: var(--text-primary) !important;
}

.json-viewer-wrapper >>> .jv-ellipsis {
  color: #9ca3af !important;
  background-color: var(--border-default) !important;
  padding: 2px 6px !important;
  border-radius: 3px !important;
  cursor: pointer !important;
}

.json-viewer-wrapper >>> .jv-ellipsis:hover {
  background-color: var(--bg-card-secondary) !important;
}

.json-viewer-wrapper >>> .jv-button {
  color: #9ca3af !important;
  background-color: var(--border-default) !important;
  border-radius: 3px !important;
}

.json-viewer-wrapper >>> .jv-button:hover {
  background-color: var(--bg-card-secondary) !important;
}

.json-viewer-wrapper >>> .jv-key {
  color: #60a5fa !important;
}

.json-viewer-wrapper >>> .jv-item.jv-string {
  color: #10b981 !important;
}

.json-viewer-wrapper >>> .jv-item.jv-number {
  color: #f59e0b !important;
}

.json-viewer-wrapper >>> .jv-item.jv-boolean {
  color: var(--primary-start) !important;
}

.json-viewer-wrapper >>> .jv-item.jv-null {
  color: #ef4444 !important;
}

.nodes-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>
